import { DebugHelper } from "@/helper/debugHelper";

// ActionScript の Base64Decoder を移植
// look at:
// https://github.com/apache/flex-sdk/blob/develop/frameworks/projects/framework/src/mx/utils/Base64Decoder.as

export class Base64Decoder
{
    //--------------------------------------------------------------------------
    //
    //  Methods
    //
    //--------------------------------------------------------------------------
    /**
     * Decodes a Base64 encoded String and adds the result to an internal
     * buffer. Strings must be in ASCII format. 
     * 
     * <p>Subsequent calls to this method add on to the internal
     * buffer. After all data have been encoded, call <code>toByteArray()</code>
     * to obtain a decoded <code>flash.utils.ByteArray</code>.</p>
     * 
     * @param encoded The Base64 encoded String to decode.
     *  
     *  @langversion 3.0
     *  @playerversion Flash 9
     *  @playerversion AIR 1.1
     *  @productversion Flex 3
     */
    public decode(encoded : string) : void
    {
        for(var i = 0; i < encoded.length; ++i)
        {
            var c = encoded.charCodeAt(i);

            if(c == Base64Decoder.ESCAPE_CHAR_CODE)
            {
                this.work[this.count++] = -1;
            }
            else if(Base64Decoder.inverse[c] != 64)
            {
                this.work[this.count++] = Base64Decoder.inverse[c];
            }
            else 
            {
                continue;
            }

            if(this.count == 4)
            {
                this.count = 0;
                this.writeByte(this.data, (this.work[0] << 2) | ((this.work[1] & 0xFF) >> 4));
                this.filled++;

                if(this.work[2] == -1)
                {
                    break;
                }

                this.writeByte(this.data, (this.work[1] << 4) | ((this.work[2] & 0xFF) >> 2));
                this.filled++;

                if(this.work[3] == -1)
                {
                    break;
                }

                this.writeByte(this.data, (this.work[2] << 6) | this.work[3]);
                this.filled++;
            }
        }
    }

    private writeByte(byteArray : Array<number>, value : number)
    {
        // 書き込む値の下位8bitを取り出す
        var byte = value & 0xFF;
        byteArray.push(byte);
    }

    /**
     * @private
     */
    public drain() : Array<number>
    {
        var result = new Array<number>();

        // 配列の全要素のコピー
        for(var i = 0; i < this.data.length; ++i)
        {
            result.push(this.data[i]);
        }

        this.filled = 0;
        return result;
    }

    /**
     * @private
     */
    public flush() : Array<number>
    {
        if (this.count > 0)
        {
            DebugHelper.trace("partialBlockDropped " + this.count);
            return [];
        }
        return this.drain();
    }

    /**
     * Clears all buffers and resets the decoder to its initial state.
     *  
     *  @langversion 3.0
     *  @playerversion Flash 9
     *  @playerversion AIR 1.1
     *  @productversion Flex 3
     */
    public reset(): void
    {
        this.data = new Array();
        this.count = 0;
        this.filled = 0;
    }

    /**
     * Returns the current buffer as a decoded <code>flash.utils.ByteArray</code>.
     * Note that calling this method also clears the buffer and resets the 
     * decoder to its initial state.
     * 
     * @return The decoded <code>flash.utils.ByteArray</code>.
     *  
     *  @langversion 3.0
     *  @playerversion Flash 9
     *  @playerversion AIR 1.1
     *  @productversion Flex 3
     */
    public toByteArray() : Array<number>
    {
        var result = this.flush();
        this.reset();
        return result;
    }

    //--------------------------------------------------------------------------
    //
    //  Private Variables
    //
    //--------------------------------------------------------------------------

    private count : number = 0;
    private data : Array<number> = new Array();
    private filled : number = 0;
    private work : Array<number> = [0, 0, 0, 0];

    private static readonly ESCAPE_CHAR_CODE : number = 61; // The '=' char

    private static readonly inverse : Array<number> = 
    [
        64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64,
        64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64,
        64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 62, 64, 64, 64, 63,
        52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 64, 64, 64, 64, 64, 64,
        64, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14,
        15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 64, 64, 64, 64, 64,
        64, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40,
        41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 64, 64, 64, 64, 64,
        64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64,
        64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64,
        64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64,
        64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64,
        64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64,
        64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64,
        64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64,
        64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64, 64
    ];
}